import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import Typography from '@mui/material/Typography';

import {
  handleBackNavAction,
  updateErrorAction,
  updateStageAction,
} from 'app-components/flux/transaction-rules/transactionRuleActions';
import { useField, useManager } from 'app-components/flux/transaction-rules/transactionRulesFormHooks';
import { useStateContext } from 'app-components/flux/transaction-rules/transactionRulesFormState';
import {
  CLEARED,
  EDITING,
  RESOLVING,
  SUBMITTING,
  VALIDATED,
} from 'app-components/flux/transaction-rules/transactionRulesTypes';
import type { TransactionRuleType } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import { cancelTransactionRuleDialog } from 'components/Dialogs/CancelTransactionRule/actions';
import { deleteTransactionRuleDialog } from 'components/Dialogs/DeleteTransactionRule/actions';
import SettingsDrawer from 'components/Drawers/Settings';

import RuleEditor from './RuleEditor';
import RuleFooter from './RuleFooter';
import RuleReview from './RuleReview';

const FormContainer = ({ isDrawerOverlay = false }: { isDrawerOverlay?: boolean }) => {
  const { state, dispatch } = useStateContext();
  const { stage, rule, onUpdate, backText, titleText, enabledOverrides } = state;
  const manager = useManager();
  const rootDispatch = useDispatch();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const pendingCloseRuleRef = useRef<TransactionRuleType | undefined>(undefined);
  const hasPendingCloseRef = useRef(false);

  const { onValueChange: payeeSource } = useField('isEnabledPayeeSource');
  const { onValueChange: amountSource } = useField('isEnabledAmountSource');
  const { onValueChange: payeeTarget } = useField('isEnabledPayeeTarget');
  const { onValueChange: coaTarget } = useField('isEnabledCoaTarget');
  const { onValueChange: tagsTarget } = useField('isEnabledTagsTarget');
  const { onValueChange: memoTarget } = useField('isEnabledMemoTarget');
  const { onValueChange: excludedTarget } = useField('isEnabledExcludedTarget');
  const { onValueChange: reviewedTarget } = useField('isEnabledReviewedTarget');

  // Override condition enabled flags if provided (e.g. when creating a rule from a transaction)
  // This is a web-only feature for now
  useLayoutEffect(() => {
    if (enabledOverrides) {
      Object.entries(enabledOverrides).forEach(([key, value]) => {
        if (typeof value === 'boolean') {
          eval(`${key}(${value})`); // eslint-disable-line no-eval
        }
      });
    }
  }, [
    enabledOverrides,
    manager,
    payeeSource,
    amountSource,
    payeeTarget,
    coaTarget,
    tagsTarget,
    memoTarget,
    excludedTarget,
    reviewedTarget,
  ]);

  const handleSubmit = useCallback(() => {
    if (stage === EDITING || stage === RESOLVING) {
      manager.submit(true); // runs field-level validation

      // Business rule validation: check source/target conditions
      // These checks must run regardless of field-level validation
      const hasPayeeSource = manager.getFieldValue('isEnabledPayeeSource');
      const hasAmountSource = manager.getFieldValue('isEnabledAmountSource');
      const hasAccountSource = manager.getFieldValue('isEnabledAccountSource');
      const hasPayeeTarget = manager.getFieldValue('isEnabledPayeeTarget');
      const hasCoaTarget = manager.getFieldValue('isEnabledCoaTarget');
      const hasTagsTarget = manager.getFieldValue('isEnabledTagsTarget');
      const hasMemoTarget = manager.getFieldValue('isEnabledMemoTarget');
      const hasExcludedTarget = manager.getFieldValue('isEnabledExcludedTarget');
      const hasReviewedTarget = manager.getFieldValue('isEnabledReviewedTarget');

      // Check: At least one source condition
      if (!hasPayeeSource && !hasAmountSource && !hasAccountSource) {
        dispatch(updateStageAction(EDITING));
        dispatch(updateErrorAction('Specify at least 1 IF condition'));
        return;
      }

      // Check: Amount cannot be the only source condition
      if (hasAmountSource && !hasPayeeSource && !hasAccountSource) {
        dispatch(updateStageAction(EDITING));
        dispatch(updateErrorAction('Combine Amount with another IF condition, like Payee or Account'));
        return;
      }

      // Check: Account cannot be the only source condition
      if (hasAccountSource && !hasPayeeSource && !hasAmountSource) {
        dispatch(updateStageAction(EDITING));
        dispatch(updateErrorAction('Combine Account with another IF condition, like Payee or Amount'));
        return;
      }

      // Check: At least one target condition
      if (
        !hasPayeeTarget &&
        !hasCoaTarget &&
        !hasTagsTarget &&
        !hasMemoTarget &&
        !hasExcludedTarget &&
        !hasReviewedTarget
      ) {
        dispatch(updateStageAction(EDITING));
        dispatch(updateErrorAction('Specify at least 1 THEN condition'));
        return;
      }

      // Check field-level validation
      if (!manager.isFormValid()) {
        dispatch(updateStageAction(EDITING));
        dispatch(updateErrorAction('Form fields need attention'));
        return;
      }
    }

    if (stage === VALIDATED) {
      dispatch(updateStageAction(SUBMITTING));
    }
  }, [dispatch, manager, stage]);

  const closeDrawer = useCallback((nextRule?: TransactionRuleType) => {
    pendingCloseRuleRef.current = nextRule;
    hasPendingCloseRef.current = true;
    setDrawerOpen(false);
  }, []);

  const handleNavigation = useCallback(() => {
    if (stage === EDITING) {
      closeDrawer(undefined);
      return;
    }

    stage && dispatch(handleBackNavAction(stage));
  }, [closeDrawer, dispatch, stage]);

  const handleDelete = useCallback(() => {
    rule?.id &&
      rootDispatch(
        deleteTransactionRuleDialog({
          ruleId: rule.id,
          source: 'ruleEdit',
          onDelete: () => closeDrawer(undefined),
        }),
      );
  }, [closeDrawer, rootDispatch, rule]);

  const handleCancel = useCallback(() => {
    // show confirmation dialog on create but not edit
    rule && rule.id
      ? closeDrawer(undefined)
      : rootDispatch(cancelTransactionRuleDialog({ onCancel: () => closeDrawer(undefined) }));
  }, [closeDrawer, rootDispatch, rule]);

  const handleDrawerExited = useCallback(() => {
    if (!hasPendingCloseRef.current) {
      return;
    }

    hasPendingCloseRef.current = false;
    onUpdate(pendingCloseRuleRef.current);
    pendingCloseRuleRef.current = undefined;
  }, [onUpdate]);

  useEffect(() => {
    if (!stage) {
      rule ? dispatch(updateStageAction(EDITING)) : closeDrawer(undefined);
    } else if (stage === 'EDITING' && !rule) {
      closeDrawer(undefined);
    } else if (stage === 'SUBMITTING' && rule) {
      // if we have a duplicateId in the state, set the rule id so we treat this as an update
      const newRule = state.duplicateId ? { ...rule, id: state.duplicateId } : rule;
      closeDrawer(newRule);
    } else if (stage === CLEARED) {
      closeDrawer(undefined);
    }
  }, [closeDrawer, dispatch, rule, stage, state]);

  const getMainContent = (): React.JSX.Element => {
    return (
      <>
        {(stage === EDITING || stage === RESOLVING) && <RuleEditor />}
        {(stage === VALIDATED || stage === SUBMITTING) && rule && <RuleReview />}
      </>
    );
  };

  return (
    <SettingsDrawer
      id='rules-settings-drawer'
      isOverlay={isDrawerOverlay}
      open={drawerOpen}
      stage={stage || 'EDITING'}
      onClose={handleNavigation}
      onDismiss={handleCancel}
      onExited={handleDrawerExited}
      header={<Typography variant='headlineMedium'>{titleText}</Typography>}
      footer={<RuleFooter onSubmit={handleSubmit} onCancel={handleCancel} onDelete={handleDelete} />}
      backText={backText}
    >
      {getMainContent()}
    </SettingsDrawer>
  );
};

export default FormContainer;
